import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { HLSProtocol, MixerMapperComp } from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import { channelActions, useRubyDispatch, useRubySelector } from "userful-chronos-common-store/dist/ruby";
import {
    createDefaultHLSStreamRequestForMapper,
    getHLSStreamDataForMapper,
} from "userful-chronos-app-common-js/dist/models/mapping/Streams";
import { MutableChannel, MutableRubySource } from "userful-chronos-app-common-js/dist/models/ruby/channel";
import { v4 as uuidv4 } from "uuid";
import React, { useEffect, useRef, useState } from "react";
import { ColumnDef, sortingFns } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import UserfulReactTable2 from "../table/v2/UserfulReactTable2";
import Switch from "react-switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleNotch, faMultiply, faPlus } from "@fortawesome/pro-regular-svg-icons";
import Button from "../button/Button";
import {
    delayItBy,
    PromiseQueue,
    requestAndWaitForResponse,
} from "userful-chronos-app-common-js/dist/message/msgQueue";
import {
    REQUEST_CREATE_MAPPER_HLS_STREAM,
    REQUEST_REMOVE_MAPPER_HLS_STREAM,
    RESPOND_MAPPER_HLS_STREAM,
    RESPOND_MAPPER_HLS_STREAM_REMOVED,
} from "userful-chronos-app-common-js/dist/message/messageTypeDefinitions/mapping/MappingMsgDefinations";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import NotesInForm from "../Widgets/Form/NotesInForm";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { canExecuteChannel } from "userful-chronos-app-common-js/dist/models/ruby/channelComputation";
import { HelpInfo } from "../sourceassets";
interface IProps {
    mapper: MixerMapperComp;
    handleClose: () => void;
}

export default function MapperHLSStreamForm(props: IProps) {
    const { t } = useTranslation();
    const rubyDispatch = useRubyDispatch();
    // filter is based on if the user has permissions to execute those channels or not
    const channels = useRubySelector((state) => state.channelStore.channels).filter((channel) =>
        canExecuteChannel(channel.userfulSecurityData),
    );
    const stream = getHLSStreamDataForMapper(props.mapper);
    const existingSelectedChannels: MutableChannel[] = channels.filter(
        (c) => c.sources.findIndex((item) => item.createdFrom.value === props.mapper.id.value) >= 0,
    );

    const [selectedChannels, setSelectedChannels] = useState<MutableChannel[]>(existingSelectedChannels);
    const [loading, setLoading] = useState<boolean>(null);
    const isMultiStream = stream && (stream.videoProtocol as HLSProtocol).multiStream;
    const [multiStream, setMultiStream] = useState(stream ? isMultiStream : true);
    const taskQueue = useRef<PromiseQueue>(new PromiseQueue());

    const getChannelSourcesToRemove = (): StringID[] => {
        const unSelectedChannels = stream
            ? existingSelectedChannels.filter(
                  (existing) => selectedChannels.findIndex((item) => item.id.value === existing.id.value) < 0,
              )
            : existingSelectedChannels;
        const foundSources = unSelectedChannels
            .map((c) => c.sources.find((s) => s.createdFrom.value === props.mapper.id.value))
            .filter((s) => s);
        return foundSources.map((s) => s.id);
    };

    const getChannelSourcesToAdd = (): {
        channelID: StringID;
        source: MutableRubySource;
    }[] => {
        if (!stream) {
            return [];
        }
        const toAddChannels = selectedChannels.filter(
            (existing) => existingSelectedChannels.findIndex((item) => item.id.value === existing.id.value) < 0,
        );
        return toAddChannels.map((c) => ({
            channelID: c.id,
            source: {
                id: { value: uuidv4() },
                name: `${props.mapper.name} HLS Stream`,
                description: `Stream from mapping group ${props.mapper.name}`,
                type: "HLS",
                url: `${getGlobalStates().host}${stream.url}`,
                createdFrom: props.mapper.id,
            },
        }));
    };

    useEffect(() => {
        if (!stream && existingSelectedChannels.length > 0) {
            for (const removeSourece of getChannelSourcesToRemove()) {
                rubyDispatch(channelActions.removeChannelSourceToServer(removeSourece));
            }
        }
    }, [stream]);

    const handleSave = () => {
        for (const removeSourece of getChannelSourcesToRemove()) {
            rubyDispatch(channelActions.removeChannelSourceToServer(removeSourece));
        }
        for (const addSource of getChannelSourcesToAdd()) {
            rubyDispatch(channelActions.addOrUpdateChannelSourceToServer(addSource));
        }
        props.handleClose();
    };

    const handleCancel = () => {
        if (!stream) {
            for (const removeSourece of getChannelSourcesToRemove()) {
                rubyDispatch(channelActions.removeChannelSourceToServer(removeSourece));
            }
        }
        props.handleClose();
    };

    const toggleStreamActive = () => {
        setLoading(true);
        if (!stream) {
            requestAndWaitForResponse(
                REQUEST_CREATE_MAPPER_HLS_STREAM,
                createDefaultHLSStreamRequestForMapper(props.mapper, multiStream),
                RESPOND_MAPPER_HLS_STREAM,
                () => true,
                `activate stream`,
            ).then(() => setLoading(false));
        } else {
            requestAndWaitForResponse(
                REQUEST_REMOVE_MAPPER_HLS_STREAM,
                props.mapper.id,
                RESPOND_MAPPER_HLS_STREAM_REMOVED,
                () => true,
                `de-activate stream`,
            ).then(() => setLoading(false));
        }
    };

    const toggleMultiStream = () => {
        const enabled = !multiStream;
        setMultiStream(enabled);

        if (!stream) {
            return;
        } else {
            setLoading(true);
            taskQueue.current.enqueue(() =>
                requestAndWaitForResponse(
                    REQUEST_REMOVE_MAPPER_HLS_STREAM,
                    props.mapper.id,
                    RESPOND_MAPPER_HLS_STREAM_REMOVED,
                    () => true,
                    `de-activate stream`,
                    5000,
                ),
            );

            taskQueue.current.enqueue(() =>
                requestAndWaitForResponse(
                    REQUEST_CREATE_MAPPER_HLS_STREAM,
                    createDefaultHLSStreamRequestForMapper(props.mapper, enabled),
                    RESPOND_MAPPER_HLS_STREAM,
                    () => true,
                    `activate stream`,
                    15000,
                ),
            );

            taskQueue.current.enqueue(() =>
                delayItBy(100).then(() => {
                    setLoading(false);
                }),
            );
        }
    };

    const toggleAllSelect = () => {
        if (selectedChannels.length > 0) {
            setSelectedChannels([]);
        } else {
            setSelectedChannels([...channels]);
        }
    };
    const getIsSelected = (value: MutableChannel): boolean =>
        selectedChannels.findIndex((c) => c.id.value === value.id.value) >= 0;
    const toggleSelected = (value: MutableChannel) => {
        if (getIsSelected(value)) {
            setSelectedChannels(selectedChannels.filter((c) => c.id.value !== value.id.value));
        } else {
            setSelectedChannels([...selectedChannels, value]);
        }
    };

    const columns = React.useMemo<ColumnDef<MutableChannel>[]>(() => {
        const result: ColumnDef<MutableChannel, any>[] = [
            {
                header: () => <div className="flex channelNameCol">{t("Shared.name")}</div>,
                accessorFn: (row) => row.name,
                id: "name",
                sortingFn: sortingFns.alphanumeric,
                cell: (info) => {
                    const value = info.getValue();
                    return (
                        <>
                            <OverlayTrigger placement="bottom" overlay={<Tooltip>{value}</Tooltip>}>
                                <div className="channelNameCol">{value}</div>
                            </OverlayTrigger>
                        </>
                    );
                },
            },
            {
                id: "stream-channels-custome-select",
                header: ({ table }) => <div className="channelSelectCol"></div>,
                maxSize: 100,
                cell: ({ row }) => (
                    <div onClick={() => toggleSelected(row.original)} className="channelSelectCol">
                        {getIsSelected(row.original) ? (
                            <FontAwesomeIcon icon={faCheck} className="channelSelected fontAwesomeCustomIcon" />
                        ) : (
                            <FontAwesomeIcon icon={faPlus} className="fontAwesomeCustomIcon" />
                        )}
                    </div>
                ),
            },
        ];

        return result;
    }, [selectedChannels]);

    const hasChanges = () => {
        if (existingSelectedChannels.length != selectedChannels.length) {
            return true;
        }
        for (const c of existingSelectedChannels) {
            if (!selectedChannels.find((item) => item.id.value === c.id.value)) {
                return true;
            }
        }
        return false;
    };

    return (
        <>
            {loading && (
                <div className="loadingContainer">
                    <FontAwesomeIcon icon={faCircleNotch} spin className="fontAwesomeCustomIcon" />
                </div>
            )}
            <div className="streamForm source-creation-wrapper">
                <div className="streamFormHeader hero-wrapper">
                    <div className="streamTitle hero-text-wrapper">
                        <div className="hero-text">{t("CommonUI.Stream.title")}</div>
                        <div className="streamDescription heading2">
                            <span>{t("CommonUI.Stream.description")}</span>
                        </div>
                    </div>
                    <div className="iconButton custom-close-btn" onClick={props.handleClose}>
                        <FontAwesomeIcon icon={faMultiply} className="fontAwesomeCustomIcon" />
                    </div>
                </div>

                <div className="hlsFormContent">
                    <div>
                        <div className="streamSecondaryTitle">
                            <span>{t("CommonUI.Stream.toggleHLS")}</span>
                        </div>
                        <div className="streamDescription heading2">
                            <span>{t("CommonUI.Stream.toggleHLSdescription")}</span>
                        </div>
                    </div>
                    <div className="streamActiveToggle">
                        <Switch
                            onChange={toggleStreamActive}
                            checked={!!stream}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={16}
                            width={32}
                            onHandleColor="#FFFFFF"
                            onColor="#23A866"
                            offColor="#C6C8CE"
                            offHandleColor="#FFFFFF"
                            className="toggle-switch"
                            borderRadius={99}
                            boxShadow="0px 2px 2px rgba(31, 39, 71, 0.1)"
                            handleDiameter={12}
                        />
                        <div className="toggleLabel">{t(stream ? "Shared.active" : "Shared.Disabled")}</div>
                    </div>
                    <NotesInForm text={t("CommonUI.Stream.Notes")} icon={faInfoCircle} />
                    <div className="streamActiveToggle">
                        <Switch
                            onChange={toggleMultiStream}
                            checked={multiStream}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={16}
                            width={32}
                            onHandleColor="#FFFFFF"
                            onColor="#23A866"
                            offColor="#C6C8CE"
                            offHandleColor="#FFFFFF"
                            className="toggle-switch"
                            borderRadius={99}
                            boxShadow="0px 2px 2px rgba(31, 39, 71, 0.1)"
                            handleDiameter={12}
                        />
                        <div className="toggleLabel">
                            {t("CommonUI.Stream.multiStream")} <HelpInfo title={t("CommonUI.Stream.multiStreamHelp")} />
                        </div>
                    </div>
                    <NotesInForm text={t("CommonUI.Stream.multiStreamNotes")} icon={faInfoCircle} />

                    {stream && (
                        <>
                            <div>
                                <div className="streamSecondaryTitle">
                                    <span>{t("CommonUI.Stream.channels")}</span>
                                </div>
                                <div className="streamDescription heading2">
                                    <span>{t("CommonUI.Stream.channelsDescription")}</span>
                                </div>
                            </div>
                            <div className="channelsTable">
                                <UserfulReactTable2
                                    selectBox={false}
                                    data={channels}
                                    columns={columns}
                                    id="stream-channels-table"
                                    noData={t("CommonUI.Stream.noChannels")}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="streamFooter button-wrapper">
                <Button variant="primary" onClick={handleSave} disabled={!hasChanges()}>
                    {t("Shared.save")}
                </Button>
                <Button variant="secondary" onClick={toggleAllSelect} disabled={selectedChannels.length < 1}>
                    <FontAwesomeIcon icon={faMultiply} className="fontAwesomeCustomIcon" />{" "}
                    {t("CommonUI.Stream.cleanChannels")}
                </Button>
                <Button variant="secondary" onClick={handleCancel}>
                    {t("Shared.cancel")}
                </Button>
            </div>
        </>
    );
}
